import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import {
  ISidePadding,
  ISideOffsets,
  Orientation,
  Side,
  IRadius,
  TranslateFunction,
} from "./models/Common"
import {
  componentWantsNoRaddiRendered,
  getBounds,
  IRoundedRectShape,
  isNonZero,
  isRoundedRect,
} from "./models/Rectangle"
import { Dimensions } from "./drawing/Dimensions"
import {
  FontContext,
  ScaleContext,
  getDrawingInfo,
  getInfo,
  IArea,
} from "./drawing/Common"
import { Radius } from "./drawing/Radius"
import { Id } from "./drawing/Id"
import { Glass } from "./components/Glass"
import { Sensor } from "./components/Sensor"
import { ActiveArea } from "./components/ActiveArea"
import { PrintedBorder } from "./components/PrintedBorder"
import { FlexibleCable } from "./components/FlexibleCable"
import { Sticker, StickerDimReference } from "./components/Sticker"
import { SolutionContext } from "./models/Form"
import PDFDocument from "pdfkit"
import SVGtoPDF from "svg-to-pdfkit"
import "./pdfkit-webpack/registerStaticFiles"
import { Descriptions } from "./drawing/Descriptions"
import { Box, Button } from "@mui/material"
import { InfoTable } from "./drawing/InfoTable"
import {
  solutionErrors,
  cutoutsAdditional,
  notesAdditional,
  objectNotesAdditional,
  objectErrorsAdditional,
  glassSensorAdditional,
  tailErrorsAdditional,
  holesAdditional,
  printedBorderSizeAdditional,
} from "./components/Additionals"
import { PrintedLogo } from "./components/PrintedLogo"
import React from "react"
import { AdditionalText } from "./drawing/AdditionalText"
import { IAdditionalNotes } from "./models/AdditionalText"
import { solutionFields } from "./models/SolutionFields"
import MontserratFont from "./Montserrat-Regular.ttf"
import { Base64Encode } from "base64-stream"
import {
  BaseComponent,
  IComponentInfo,
  IComponentLabels,
  IComponentShape,
} from "./components/Base"
import { ProductNotes } from "./drawing/ProductNotes"
import { ProductType } from "./models/Product"
import { GlassLayer } from "./components/Layers/GlassLayer"
import {
  getFirstReferenceGlass,
  GlassOrder,
  stringToGlassOrder,
} from "./models/Layers"
import { ExtraLayer } from "./components/Layers/ExtraLayer"
import { SensorLayer } from "./components/Layers/SensorLayer"
import { ISolution } from "./models/Solution"
import { useTranslation } from "react-i18next"
import EmptyDrawing from "./DrawingEmpty"

interface IDrawingProps {
  admin: boolean
  renderEmpty: boolean
  width: number
  solutionFinal?: ISolution
  test?: boolean
  onlyDrawing?: boolean,
  fonstSizeSet?: number
}

// Define the methods exposed by Drawing
export interface DrawingHandle {
  getPDF: () => Promise<string>
}

const Drawing = forwardRef<DrawingHandle, IDrawingProps>(function Drawing(
  {
    admin,
    renderEmpty,
    width,
    solutionFinal,
    test,
    onlyDrawing,
    fonstSizeSet
  }: IDrawingProps,
  ref
) {
  const { t } = useTranslation()
  const tf: TranslateFunction = t

  const [detailDim, setDetailDim] = useState<{ height: number; width: number }>(
    { height: 0, width: 0 }
  )
  const [detailPosX, setDetailPosX] = useState<number>(750)
  //TODO get solution from useContext
  const solutionContext = useState(useContext(SolutionContext))[0]
  const solution = solutionFinal === undefined ? solutionContext : solutionFinal
  const [fontBuffer, setFontBuffer] = useState<ArrayBuffer | undefined>(
    undefined
  )
  const fontSize = ((fonstSizeSet || 9) / 1000) * width
  let canvasW = width
  let canvasH = canvasW / Math.sqrt(2) //793

  useEffect(() => {
    const ugh = async () => {
      setFontBuffer(
        await fetch(MontserratFont).then((res) => res.arrayBuffer())
      )
    }
    ugh()
  }, [])

  const downloadFromDrawing = () => {
    if (!svgRef.current) {
      return
    }

    const wTmp = canvasW
    const hTmp = canvasH
    canvasW = 1122
    canvasH = canvasW / Math.sqrt(2)
    var finalString = ""

    const doc = new PDFDocument({ size: "A4", layout: "landscape" })
    var stream = doc.pipe(new Base64Encode())
    stream.on("data", function (chunk) {
      finalString += chunk
    })
    stream.on("end", function () {
      const link = document.createElement("a")
      link.href = `data:application/pdf;base64,${finalString}`
      link.download = "Technical-drawing-test-download"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    })

    doc.registerFont("Montserrat", fontBuffer)
    SVGtoPDF(doc, svgRef.current, 0, 0, { useCSS: true })
    doc.end()

    canvasW = wTmp
    canvasH = hTmp
  }

  const getPDF = () => {
    return new Promise<string>((resolve, reject) => {
      if (svgRef.current === null) {
        console.log("SVG is null")
        reject(new Error("SVG element is null"))
        return null
      }
      console.log("Generating PDF...")
      let base64str = ""
      const doc = new PDFDocument({ size: "A4", layout: "landscape" })
      var stream = doc.pipe(new Base64Encode())
      stream.on("data", function (chunk) {
        base64str += chunk
      })
      stream.on("end", function () {
        resolve(base64str)
      })

      if (fontBuffer) {
        doc.registerFont("Montserrat", fontBuffer)
      }
      SVGtoPDF(doc, svgRef.current, 0, 0, { useCSS: true })
      doc.end()
    })
  }

  useImperativeHandle(ref, () => ({
    getPDF,
  }))

  useEffect(() => {
    if (solution.sensor.cable.detail === null) {
      setDetailDim({ height: 0, width: 0 })
      return
    }
    const img = new Image()
    img.src = solution.sensor.cable.detail
    img.onload = () => {
      const { height, width } = getHW(
        img.naturalHeight,
        img.naturalWidth,
        solution.product.notes + solution.product.adminNotes !== ""
      )
      setDetailDim({ height: height, width: width })
      const x = 0.99 * canvasW - width
      setDetailPosX(x)
    }

    const getHW = (naturalH: number, naturalW: number, notes: boolean) => {
      const maxW = 0.35 * canvasW
      const maxH = (!notes ? 0.35 : 0.2) * canvasW

      if (naturalH <= maxH && naturalW <= maxW) {
        return { height: naturalH, width: naturalW }
      }

      const scaleH = maxH / naturalH
      const scaleW = maxW / naturalW
      const scale = Math.min(scaleH, scaleW)
      const H = naturalH * scale
      const W = naturalW * scale
      return { height: H, width: W }
    }
  }, [solution.sensor.cable.detail, solution.product.notes, solution.product.adminNotes, canvasW])

  /*
    useEffect(() => {
        setPageTitle("Drawing")
        const fetchData = async (identifier: string) => {
            console.log('Loading drawing', identifier)
            const result = await fetch(`/api/drawing/${identifier}`)
            if (result.status === 200) {
                const data = await result.json()
                console.log(data)
                setData(data)
            }
        }
        if (identifier !== undefined) {
            fetchData(identifier)
        }
    }, [identifier]);
    */
  const svgRef = useRef<SVGSVGElement>(null)
  const downloadRef = useRef<HTMLAnchorElement>(null)

  if (renderEmpty)
    return (
      <EmptyDrawing
        infoMsg={tf("fill_in_all_details")}
        canvasH={canvasH}
        canvasW={canvasW}
      />
    )

  console.log(solution)

  const frontComponents: Array<BaseComponent> = []

  const isGlassNonZeroDim = [
    GlassOrder.Front,
    GlassOrder.Middle,
    GlassOrder.Rear,
  ].map(
    (i: GlassOrder) =>
      Number(solution.glass.height[i]) !== 0 &&
      Number(solution.glass.width[i]) !== 0
  )
  const withGlass = solution.glass.withGlass
  const withPrinting =
    solution.printing.withPrinting === true &&
    withGlass &&
    isGlassNonZeroDim[stringToGlassOrder(solution.printing.onGlass)]
  const withObject = solution.printing.withLogo === true && withPrinting
  const withSensor = solution.sensor.withSensor
  const typeO = solution.product.type === ProductType.FortouchO
  const independentGlass = solution.glass.independentWH && typeO && withGlass

  ///////FRONT VIEWS
  /*
         IDs (2x)
      2. frontglass sideview
      3. middleglass sideview
      4. rearglass sideview
      5. printed border
      6. Extra layers
      7. sensor
      8. active area
      9. PET part
      10. Flex calbe
    11-n. Images
    */

  const frontGlass =
    withGlass && isGlassNonZeroDim[GlassOrder.Front]
      ? new Glass(4, solution, GlassOrder.Front)
      : undefined
  if (frontGlass) frontComponents.push(frontGlass)
  const middleGlass =
    independentGlass && isGlassNonZeroDim[GlassOrder.Middle]
      ? new Glass(6, solution, GlassOrder.Middle)
      : undefined
  if (middleGlass) frontComponents.push(middleGlass)
  const rearGlass =
    independentGlass && isGlassNonZeroDim[GlassOrder.Rear]
      ? new Glass(8, solution, GlassOrder.Rear)
      : undefined
  if (rearGlass) frontComponents.push(rearGlass)

  const printedBorder =
    withPrinting && withGlass
      ? new PrintedBorder(
          10,
          solution.printing.color || "RAL9005",
          [frontGlass, middleGlass, rearGlass][
            stringToGlassOrder(solution.printing.onGlass)
          ]!,
          solution.printing.doublePrint,
          solution.printing.frameThickness,
          stringToGlassOrder(solution.printing.onGlass),
          t
        )
      : undefined
  if (printedBorder) {
    frontComponents.push(printedBorder)
  }

  const activeArea = withSensor
    ? new ActiveArea(16, solution, t, printedBorder, printedBorder?.shape)
    : undefined
  if (activeArea) frontComponents.push(activeArea)

  const sensor = activeArea
    ? new Sensor(
        14,
        solution,
        printedBorder?.shape ?? activeArea.shape,
        activeArea,
        frontGlass,
        t
      )
    : undefined
  if (sensor) frontComponents.push(sensor)

  const sticker = sensor ? new Sticker(18, solution, sensor, t) : undefined
  if (sticker) {
    frontComponents.push(sticker)
  }

  const stickerDimRef =
    sensor && solution.sensor.tail.centralOffset > 0
      ? new StickerDimReference(solution, sticker!)
      : undefined
  if (stickerDimRef) {
    if (stickerDimRef!.labels.dimensions)
      stickerDimRef!.labels.dimensions.reference = {
        width: 0,
        height: 0,
        centralOffsetX: activeArea!.shape.centralOffsetX,
        centralOffsetY: activeArea!.shape.centralOffsetY,
      } as IComponentShape
    frontComponents.push(stickerDimRef!)
  }

  const flexCable = sticker
    ? new FlexibleCable(20, solution, sticker, t)
    : undefined
  if (flexCable) frontComponents.push(flexCable)

  const glassWObjects =
    stringToGlassOrder(solution.printing.onGlass) === GlassOrder.Front
      ? frontGlass
      : stringToGlassOrder(solution.printing.onGlass) === GlassOrder.Middle
      ? middleGlass
      : rearGlass

  const printedComponents: Array<BaseComponent> = []
  if (withObject) {
    solution.printing.objects.forEach((obj, i) => {
      frontComponents.push(
        new PrintedLogo(
          i + 22,
          obj,
          glassWObjects!,
          canvasW,
          printedBorder!.labels.dimensions!.reference!,
          t
        )
      )
      printedComponents.push(frontComponents[frontComponents.length - 1])
    })
  }

  frontComponents.sort((a, b) => a.info.id - b.info.id)

  if (solution.product.backView) {
    frontComponents.forEach((c) => {
      if (
        (c.shape as IRoundedRectShape).radius !== undefined &&
        (c.shape as IRoundedRectShape).radius !== null
      ) {
        let tmp = ((c.shape as IRoundedRectShape).radius as IRadius).tl
        ;((c.shape as IRoundedRectShape).radius as IRadius).tl = (
          (c.shape as IRoundedRectShape).radius as IRadius
        ).tr
        ;((c.shape as IRoundedRectShape).radius as IRadius).tr = tmp

        tmp = ((c.shape as IRoundedRectShape).radius as IRadius).bl
        ;((c.shape as IRoundedRectShape).radius as IRadius).bl = (
          (c.shape as IRoundedRectShape).radius as IRadius
        ).br
        ;((c.shape as IRoundedRectShape).radius as IRadius).br = tmp
      }
      if (c.shape.centralOffsetX !== undefined) c.shape.centralOffsetX *= -1
    })
  }

  solution.layers.layersArr
    .sort((a, b) => a.order - b.order)
    .forEach((layer, i) => {
      switch (layer.id) {
        case "G0": {
          if (frontGlass?.labels.dimensions)
            frontGlass.labels.dimensions.order = i
          if (
            solution.printing.onGlass === "Front glass" &&
            printedBorder?.labels.dimensions
          ) {
            printedBorder.labels.dimensions.order = i
            printedComponents.forEach((printObj) => {
              if (printObj.labels.dimensions)
                printObj.labels.dimensions.order = i
            })
          }
          break
        }
        case "G1": {
          if (middleGlass?.labels.dimensions)
            middleGlass.labels.dimensions.order = i
          if (
            solution.printing.onGlass === "Middle glass" &&
            printedBorder?.labels.dimensions
          ) {
            printedBorder.labels.dimensions.order = i
            printedComponents.forEach((printObj) => {
              if (printObj.labels.dimensions)
                printObj.labels.dimensions.order = i
            })
          }
          break
        }
        case "G2": {
          if (rearGlass?.labels.dimensions)
            rearGlass.labels.dimensions.order = i
          if (
            solution.printing.onGlass === "Rear glass" &&
            printedBorder?.labels.dimensions
          ) {
            printedBorder.labels.dimensions.order = i
            printedComponents.forEach((printObj) => {
              if (printObj.labels.dimensions)
                printObj.labels.dimensions.order = i
            })
          }
          break
        }
        case "SN": {
          if (activeArea?.labels.dimensions)
            activeArea.labels.dimensions.order = i
          if (sensor?.labels.dimensions) sensor.labels.dimensions.order = i
          if (sticker?.labels.dimensions)
            sticker.labels.dimensions.order = i + 0.2
          if (stickerDimRef?.labels.dimensions)
            stickerDimRef.labels.dimensions.order = i + 0.3
          if (flexCable?.labels.dimensions)
            flexCable.labels.dimensions.order = i + 0.1
          break
        }
      }
    })

  const componentsWDimensions = frontComponents
    .filter((c) => c.labels && c.labels.dimensions)
    .sort((a, b) => a.info.id - b.info.id)
  const measureOffsets: ISideOffsets[] = []

  const offsets = {
    t: -0.0265 * width,
    b: 0.0265 * width,
    l: -0.0265 * width,
    r: 0.0265 * width,
  } as ISidePadding

  componentsWDimensions
    .sort((a, b) => a.labels.dimensions!.order - b.labels.dimensions!.order)
    .forEach((c) => {
      measureOffsets.push({ ...offsets })

      if (c.labels.dimensions?.sides.includes(Side.Top)) offsets.t -= 22
      if (c.labels.dimensions?.sides.includes(Side.Bottom)) offsets.b += 22
      if (c.labels.dimensions?.sides.includes(Side.Left)) offsets.l -= 22
      if (c.labels.dimensions?.sides.includes(Side.Right)) offsets.r += 22
    })

  const productNotesArea =
    solution.product.notes + solution.product.adminNotes === ""
      ? ({ width: 0, height: 0 } as IArea)
      : ({
          width: Math.max(0.25 * canvasW, detailDim.width),
          height: 50 + 0.15 * canvasW,
          offsetX: canvasW - Math.max(0.25 * canvasW, detailDim.width),
          offsetY: 140 + detailDim.height,
        } as IArea)

  const imageNotesW = Math.max(
    productNotesArea.width,
    solution.sensor.cable.detail == null || !solution.sensor.withSensor
      ? 0
      : detailDim.width
  )
  const frontShift = imageNotesW === 0 ? 0 : imageNotesW + 0.05 * canvasW

  const drawingFrontArea = {
    width: !onlyDrawing ? canvasW - frontShift : canvasW,
    height: (!onlyDrawing ? 0.6 : 0.8) * canvasH,
    offsetX: 0,
    offsetY: 20,
  } as IArea

  const drawingProfileArea = {
    width: drawingFrontArea.width,
    height: (!onlyDrawing ? 0.16 : 0.2) * canvasH,
    offsetX: drawingFrontArea.offsetX,
    offsetY: drawingFrontArea.height + drawingFrontArea.offsetY,
  } as IArea

  const notesArea = {
    width: 0.3 * canvasW,
    height: canvasH - (drawingProfileArea.height + drawingProfileArea.offsetY),
    offsetX: 20,
    offsetY: drawingFrontArea.height + drawingProfileArea.height,
  } as IArea

  const infoTableArea = {
    width: 0.65 * canvasW - 20,
    height: 0.24 * canvasH - 20,
    offsetX: 0.35 * canvasW,
    offsetY: drawingFrontArea.height + drawingProfileArea.height,
  } as IArea

  const {
    scale: scaleFront,
    drawingBounds,
    componentBounds,
    drawingOrientation,
  } = getDrawingInfo(
    frontComponents,
    drawingFrontArea,
    { t: fontSize, l: fontSize, r: fontSize, b: fontSize },
    measureOffsets
  )
  const { scale: scaleProfile } = getInfo(scaleFront, drawingProfileArea, {
    t: 0,
    l: 0,
    r: 0,
    b: 0,
  })

  ///////Layer ordering
  const profileOffset =
    -0.5 * (componentBounds.x + componentBounds._x) -
    0.5 * scaleFront.size.invert(offsets.r + offsets.l)
  const profileComponents: Array<BaseComponent> = []

  let profileGlassFront: BaseComponent | undefined = undefined,
    profileGlassMiddle: BaseComponent | undefined = undefined,
    profileGlassRear: BaseComponent | undefined = undefined,
    profileSensor: BaseComponent | undefined = undefined,
    referenceGlass: BaseComponent | undefined = getFirstReferenceGlass(
      solution.layers.layersArr,
      frontGlass,
      middleGlass,
      rearGlass
    ),
    el_index: number | undefined = undefined

  solution.layers.layersArr
    .sort((a, b) => a.order - b.order)
    .forEach((layer, i) => {
      const currentYOffset = profileComponents.reduce(
        (prev, current) => (prev += current.shape.height),
        0
      )
      switch (layer.id) {
        case "G0": {
          profileGlassFront = new GlassLayer(
            layer.thickness,
            GlassOrder.Front,
            4,
            solution,
            profileOffset,
            currentYOffset,
            t
          )
          referenceGlass = frontGlass
          if (profileGlassFront.labels.dimensions)
            profileGlassFront.labels.dimensions.order = i
          profileComponents.push(profileGlassFront)
          break
        }
        case "G1": {
          profileGlassMiddle = new GlassLayer(
            layer.thickness,
            GlassOrder.Middle,
            6,
            solution,
            profileOffset,
            currentYOffset,
            t
          )
          referenceGlass = middleGlass
          if (profileGlassMiddle.labels.dimensions)
            profileGlassMiddle.labels.dimensions.order = i
          profileComponents.push(profileGlassMiddle)
          break
        }
        case "G2": {
          profileGlassRear = new GlassLayer(
            layer.thickness,
            GlassOrder.Rear,
            8,
            solution,
            profileOffset,
            currentYOffset,
            t
          )
          referenceGlass = rearGlass
          if (profileGlassRear.labels.dimensions)
            profileGlassRear.labels.dimensions.order = i
          profileComponents.push(profileGlassRear)
          break
        }
        case "EL": {
          el_index = profileComponents.length
          const el = new ExtraLayer(
            layer.thickness,
            12,
            solution,
            profileOffset,
            currentYOffset,
            profileComponents.length > 0
              ? profileComponents[profileComponents.length - 1].shape.width
              : frontGlass!.shape.width,
            t
          )
          profileComponents.push(el)
          if (el.labels.dimensions) el.labels.dimensions.order = i
          break
        }
        case "SN": {
          profileSensor = new SensorLayer(
            layer.thickness,
            sensor!.info.id,
            profileOffset + sensor!.shape.centralOffsetX,
            currentYOffset,
            sensor!.shape.width
          )
          if (activeArea?.labels.dimensions)
            activeArea.labels.dimensions.reference = sensor?.shape
          if (sensor?.labels.dimensions)
            sensor.labels.dimensions.reference = referenceGlass?.shape
          if (profileSensor.labels.dimensions)
            profileSensor.labels.dimensions.order = i
          profileComponents.push(profileSensor)
        }
      }
    })
  //Fix extralayer offset (if is right on sensor) and width (which is layer belows width)
  if (el_index !== undefined) {
    if (
      el_index !== profileComponents.length - 1 &&
      profileComponents[el_index + 1].info.id === sensor?.info.id
    ) {
      profileComponents[el_index].shape.centralOffsetX +=
        sensor.shape.centralOffsetX
    }
    if (el_index !== profileComponents.length - 1) {
      profileComponents[el_index].shape.width =
        profileComponents[el_index + 1].shape.width
    } else if (frontGlass) {
      profileComponents[el_index].shape.width = frontGlass.shape.width
    }
  }

  //Shift all layers up
  const profileSectionH =
    solution.layers.totalThicknes + profileComponents.length * 4
  profileComponents.forEach((comp) => {
    comp.shape.centralOffsetY -= profileSectionH / 2
  })
  const entireProfile = new BaseComponent(
    {
      id: -10,
      color: "#000",
      description: "",
    } as IComponentInfo,
    {
      width: Math.max(...profileComponents.map((c) => c.shape.width)),
      height: profileComponents.reduce(
        (sum, current) => (sum += current.shape.height),
        0
      ),
      centralOffsetX: profileOffset,
      centralOffsetY: 0,
    } as IComponentShape,
    {} as IComponentLabels
  )

  ///////
  const radiiComponents = frontComponents.filter(
    (c) =>
      isRoundedRect(c.shape) &&
      isNonZero(c.shape.radius) &&
      !componentWantsNoRaddiRendered(c.shape)
  )

  //Give all components correct id
  ;[...frontComponents, ...profileComponents]
    .filter((c) => c.info.description !== "")
    .sort((a, b) => a.info.id - b.info.id)
    .forEach((c, i) => (c.info.id = i + 1))

  //Id paired components
  try {
    if (frontGlass) frontGlass.info.id = profileGlassFront!.info.id
    if (middleGlass) middleGlass.info.id = profileGlassMiddle!.info.id
    if (rearGlass) rearGlass.info.id = profileGlassRear!.info.id
    if (sensor) profileSensor!.info.id = sensor.info.id
  } catch (e) {
    console.error(e)
    //this should never happen, since if sensor is rendered,
    //profile sensor definitely should be rendered as well

    //Although profile middle glass and profile rear glass can
    //be rendered without middla glass and rear glass, if
    //independet glass is false
  }

  const describedComponents = [...frontComponents, ...profileComponents]
    .filter((c) => c.info.description !== "")
    .sort((a, b) => a.info.id - b.info.id)
    .sort((a, b) => a.labels.dimensions!.order - b.labels.dimensions!.order)
  describedComponents.map((c, i) => (c.info.id = i + 1))

  const allAdditionals = [
    ...solutionErrors(solutionFields, solution),
    ...glassSensorAdditional(frontGlass, sensor, activeArea, printedBorder),
    ...objectErrorsAdditional(
      solution.printing?.objects,
      solution.glass,
      solution.printing?.frameThickness,
      solution.printing?.withLogo
    ),
    ...objectNotesAdditional(
      solution.printing?.objects,
      solution.printing?.withLogo
    ),
    cutoutsAdditional(solution.customization),
    holesAdditional(solution.customization),
    ...tailErrorsAdditional(solution.sensor.tail, solution.sensor),
    printedBorderSizeAdditional(
      glassWObjects,
      printedBorder,
      stringToGlassOrder(solution.printing.onGlass)
    ),
    notesAdditional(solution.product.notes + solution.product.adminNotes),
  ].filter((t) => t != null)

  //`fill in all the details and you will see your proposal here`
  const infoMsg =
    !solution.glass.withGlass && !solution.sensor.withSensor
      ? tf("no_glass_or_sensor")
      : isGlassNonZeroDim.every((b) => b === false) && withGlass
      ? tf("all_glass_0x0")
      : undefined

  if (infoMsg)
    return (
      <EmptyDrawing infoMsg={infoMsg} canvasH={canvasH} canvasW={canvasW} />
    )

  return (
    <FontContext.Provider value={{ size: fontSize }}>
      {test && (
        <Box sx={{ p: 2, display: "flex", columnGap: 1 }}>
          <Button variant="contained" onClick={() => downloadFromDrawing()}>
            Download form drawing
          </Button>

          <a
            ref={downloadRef}
            style={{ display: "none" }}
            download="technical-drawing.pdf"
            href="/#"
          >
            {" "}
          </a>
        </Box>
      )}

      <svg
        ref={svgRef}
        width={canvasW}
        height={canvasH}
        style={{
          border: !onlyDrawing ? "1px solid #dfe0f2" : "",
          borderRadius: !onlyDrawing ? "35px" : "0",
        }}
      >
        <g id="drawing-front">
          <ScaleContext.Provider value={scaleFront}>
            <text
              x={scaleFront.posX(
                drawingOrientation === Orientation.Landscape
                  ? drawingBounds._x
                  : drawingBounds._y
              )}
              y={2 * fontSize}
              textAnchor="middle"
              fontSize={fontSize}
              fill="#000"
            >
              {solution.product.backView ? tf("back") : tf("front")}{" "}
              {tf("view")}
            </text>

            <g className="components">
              {frontComponents.map((c) => c.render())}
            </g>
            <g className="dimensions">
              {componentsWDimensions
                .sort(
                  (a, b) =>
                    a.labels.dimensions!.order - b.labels.dimensions!.order
                )
                .map(
                  (c, i) =>
                    c.labels.dimensions && (
                      <Dimensions
                        key={i}
                        measuredShape={c.shape}
                        measuredInfo={c.info}
                        referentialShape={
                          c.labels.dimensions.reference ? c.labels.dimensions.reference :
                          frontGlass?.shape ? frontGlass.shape :
                          c.shape
                        }
                        componentBounds={componentBounds}
                        maxTickLength={15}
                        offset={measureOffsets[i]}
                        diff={c.labels.dimensions.diff}
                        sides={c.labels.dimensions.sides}
                      />
                    )
                )}
            </g>
            <g className="ids">
              {frontComponents.map(
                (c, i) =>
                  c.labels.id && (
                    <Id
                      key={i}
                      shape={c!.shape}
                      info={c!.info}
                      {...c!.labels.id}
                    />
                  )
              )}
            </g>
            <g className="radii">
              {radiiComponents.map((c, i) => (
                <Radius
                  key={i}
                  measuredShape={c!.shape}
                  measuredInfo={c!.info}
                  arrowLength={50}
                />
              ))}
            </g>
          </ScaleContext.Provider>
        </g>

        {solution.sensor.cable.detail != null && solution.sensor.withSensor && (
          <g id="drawing-detail">
            <text
              x={detailPosX}
              y={2 * fontSize}
              fontSize={fontSize}
              fill="#000"
            >
              {tf("f_cable_detail")}
            </text>
            <image
              height={`${detailDim.height}px`}
              href={solution.sensor.cable.detail}
              x={`${detailPosX}px`}
              y={"100px"}
            />
          </g>
        )}

        {!onlyDrawing && solution.product.notes + solution.product.adminNotes !== "" && (
          <g id="notes">
            <ProductNotes
              area={productNotesArea}
              fontSize={1.5 * fontSize}
              fill="#000"
              text={solution.product.adminNotes + "\n" + solution.product.notes}
            />
          </g>
        )}

        <g id="drawing-profile">
          <text
            x={scaleProfile.posX(getBounds(entireProfile.shape)._x)}
            y={drawingProfileArea.offsetY + 3 * fontSize}
            fontSize={fontSize}
            fill="#000"
          >
            {tf("side_view", {
              thickness: solution.layers.totalThicknes.toFixed(2),
            })}
          </text>
          <ScaleContext.Provider value={scaleProfile}>
            {profileComponents.map((c) => c.render())}
            {profileComponents.map((c, i) => (
              <Dimensions
                measuredShape={c!.shape}
                measuredInfo={c!.info}
                referentialShape={c!.shape}
                componentBounds={drawingBounds}
                maxTickLength={15}
                offset={{ t: 0, b: 0, l: 0, r: -(45 + i * 22) }}
                diff={true}
                sides={c!.labels.dimensions?.sides ?? []}
              />
            ))}
            {profileComponents
              .filter((c) => c.labels.id !== undefined)
              .map((c) => (
                <Id
                  shape={c.shape}
                  info={c.info}
                  fromSide={c.labels.id!.fromSide}
                  locationDirection={c.labels.id!.locationDirection}
                  {...c.labels.id}
                />
              ))}
          </ScaleContext.Provider>
        </g>

        {!onlyDrawing && (
          <FontContext.Provider value={{ size: fontSize * 1.5 }}>
            <g id="descriptions">
              <Descriptions area={notesArea} components={describedComponents} />
            </g>
          </FontContext.Provider>
        )}

        {!onlyDrawing && (
          <g id="info-table">
            <InfoTable area={infoTableArea} solution={solution} t={t} />
          </g>
        )}
      </svg>

      <Box>
        {!onlyDrawing && allAdditionals.map((obj: IAdditionalNotes | undefined, i) => {
          if (obj === undefined) return <></>
          return (
            <AdditionalText
              text={obj.text}
              type={obj.type}
              width={canvasW - 30}
            />
          )
        })}
      </Box>
    </FontContext.Provider>
  )
})

export default Drawing
